import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'

interface KnowledgeBaseFileUploadProps {
  fileName?: string
  plan: string
  onUploadSuccess: (fileName: string, fileSize: number, tokens: number) => void
  onRemoveSuccess: () => void
}

const PLAN_LIMITS: Record<string, { size: number; label: string }> = {
  free: { size: 0, label: '0 KB' },
  starter: { size: 20480, label: '20 KB' },
  growth: { size: 81920, label: '80 KB' },
  pro: { size: 81920, label: '80 KB' },
  enterprise: { size: 409600, label: '400 KB' },
}

export const KnowledgeBaseFileUpload: React.FC<KnowledgeBaseFileUploadProps> = ({
  fileName,
  plan,
  onUploadSuccess,
  onRemoveSuccess,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [dragActive, setDragActive] = useState(false)

  const planKey = plan.toLowerCase()
  const limit = PLAN_LIMITS[planKey] || PLAN_LIMITS.starter
  const canUpload = limit.size > 0

  const handleFile = async (file: File) => {
    setUploadError('')
    setIsUploading(true)

    try {
      // Validate file type: only Markdown files (.md or .markdown)
      const lowerName = file.name.toLowerCase()
      if (!lowerName.endsWith('.md') && !lowerName.endsWith('.markdown')) {
        setUploadError(t('widget.uploadOnlyMd') || 'Only Markdown files (.md, .markdown) are allowed')
        setIsUploading(false)
        return
      }

      // Validate file size
      if (file.size > limit.size) {
        setUploadError(
          `${t('widget.fileTooLarge') || 'File too large'}. ${t('widget.planLimit') || 'Your plan limit'}: ${limit.label}`
        )
        setIsUploading(false)
        return
      }

      // Create form data
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/widgets/knowledge-base/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.data.success) {
        onUploadSuccess(
          response.data.data.fileName,
          response.data.data.fileSize,
          response.data.data.tokens
        )
      }
    } catch (error: any) {
      setUploadError(
        error.response?.data?.error || t('widget.uploadError') || 'Failed to upload file'
      )
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleRemove = async () => {
    try {
      await api.delete('/widgets/knowledge-base/file')
      onRemoveSuccess()
    } catch (error: any) {
      setUploadError(error.response?.data?.error || t('widget.removeError') || 'Failed to remove file')
    }
  }

  if (!canUpload) {
    return (
      <div className="bg-[#1a1a25] border border-white/10 rounded-lg p-4 text-center">
        <p className="text-gray-400 text-sm">
          {planKey === 'growth'
            ? t('widget.fileUploadGrowth') || 'File upload is available on Pro and higher plans'
            : t('widget.fileUploadPro') || 'File upload is available on Starter and higher plans'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* File upload area */}
      {!fileName ? (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? 'border-[#ff6600] bg-[#ff6600]/5'
              : 'border-white/20 hover:border-white/40'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,text/markdown,text/plain"
            onChange={handleChange}
            className="hidden"
          />
          <div className="text-3xl mb-2">📄</div>
          <p className="text-gray-300 text-sm mb-1">
            {t('widget.dragDropMd') || 'Drag & drop a .md file here'}
          </p>
          <p className="text-gray-500 text-xs mb-3">
            {t('widget.orClickToBrowse') || 'or click to browse'}
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            {isUploading
              ? t('widget.uploading') || 'Uploading...'
              : t('widget.selectFile') || 'Select File'}
          </button>
          <p className="text-gray-600 text-xs mt-2">
            {t('widget.maxFileSize') || 'Max file size'}: {limit.label}
          </p>
        </div>
      ) : (
        /* Uploaded file display */
        <div className="bg-[#1a1a25] border border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="text-white text-sm font-medium">{fileName}</p>
                <p className="text-gray-500 text-xs">
                  {t('widget.uploaded') || 'Uploaded'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="text-red-400 hover:text-red-300 text-sm px-3 py-1 rounded-lg hover:bg-red-400/10 transition-colors"
            >
              {t('widget.remove') || 'Remove'}
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {uploadError && (
        <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2 rounded-lg">
          {uploadError}
        </p>
      )}
    </div>
  )
}
